import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  SalesBoosterTabBar,
  type ActiveTab,
} from "./sales-booster/SalesBoosterTabBar";
import { AbandonedCartSection } from "./sales-booster/AbandonedCartSection";

// ─── Types ────────────────────────────────────────────────────────────────────

type SIconType = React.JSX.IntrinsicElements["s-icon"]["type"];

// ─── Feature Card ─────────────────────────────────────────────────────────────

interface FeatureCardProps {
  iconType: SIconType;
  title: string;
  description: React.ReactNode;
  configLabel: string;
  onConfigure?: () => void;
}

function FeatureCard({
  iconType,
  title,
  description,
  configLabel,
  onConfigure,
}: FeatureCardProps) {
  return (
    <s-grid gridTemplateColumns="1fr auto" gap="large-400">
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-icon type={iconType} />
          <strong>{title}</strong>
        </s-stack>

        <s-text color="subdued">{description}</s-text>

        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-button variant="primary" icon="cart" onClick={onConfigure}>
            {configLabel}
          </s-button>
        </s-stack>
      </s-stack>
    </s-grid>
  );
}

// ─── Help Banner ──────────────────────────────────────────────────────────────

function HelpBanner() {
  return (
    <s-banner
      heading="¿Necesitas ayuda con los nuevos Upsells?"
      tone="info"
      dismissible
    >
      <s-stack direction="block" gap="base">
        ¡Contáctenos, nuestros agentes de soporte estarán encantados de
        ayudarle!
        <s-stack direction="inline" gap="base">
          <s-button icon="chat">Contáctanos</s-button>
        </s-stack>
      </s-stack>
    </s-banner>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const SalesBoosterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as ActiveTab) ?? "upsells";

  return (
    <div>
      <s-page heading="Impulsor de ventas">
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          {/* ── Upsells & Downsells ── */}
          {activeTab === "upsells" && (
            <>
              <h2>
                <strong>Upsells &amp; Downsells</strong>
              </h2>

              <s-section>
                <FeatureCard
                  iconType="cart-up"
                  title="Upsells 1-Click"
                  description="Estos upsells aparecerán en un popup antes o después de que tus clientes compren en el formulario COD. Se pueden usar para vender productos relacionados a tus clientes para aumentar tu AOV."
                  configLabel="Configurar Upsells 1-Click"
                  onConfigure={() => navigate("/sales-booster/upsells-1-click")}
                />
              </s-section>

              <s-section>
                <FeatureCard
                  iconType="checkbox"
                  title="Upsells 1-Tick"
                  description={
                    <>
                      Estos upsells aparecerán dentro de tu formulario COD y
                      puede usarlos para aumentar tu AOV con complementos.
                      Ejemplos: <strong>Protección de envío</strong>,{" "}
                      <strong>Envoltorio de regalo</strong>,{" "}
                      <strong>Garantía ampliada</strong>
                    </>
                  }
                  configLabel="Configurar Upsells 1-Tick"
                  onConfigure={() => navigate("/sales-booster/upsells-1-tick")}
                />
              </s-section>

              <s-section>
                <FeatureCard
                  iconType="cart-down"
                  title="Downsells"
                  description="Los downsell son popups que ofrecen un descuento a sus clientes para completar su pedido cuando cierran el formulario. Puede usarlos para recuperar las ventas de los clientes que abrieron el formulario pero luego lo cerraron."
                  configLabel="Configurar Downsells"
                  onConfigure={() => navigate("/sales-booster/downsells")}
                />
              </s-section>

              <HelpBanner />
            </>
          )}

          {/* ── Abandoned Cart ── */}
          {activeTab === "abandoned-cart" && <AbandonedCartSection />}
        </s-stack>
      </s-page>
    </div>
  );
};

export default SalesBoosterPage;
